import type { ResponsePrepTime, WordLimit } from "@memorang/types";
import { useRef } from "react";
import { useDevice } from "../../../../useDevice";
import AudioPlayerContainer, {
	type AudioPlayerRef,
} from "../../../AudioPlayerContainer";
import Box from "../../../Box";
import usePrepSequence from "../../hooks/usePrepSequence";
import useWritingTimer from "../../hooks/useWritingTimer";
import WritingInput from "../WritingInput";
import { ItemInstruction } from "./pte/ItemInstruction";
import { PrepCountdownBanner } from "./pte/PrepCountdownBanner";
import { SubInstruction } from "./pte/SubInstruction";

type Props = {
	instruction: string;
	subInstruction?: string;
	audioUri: string;
	value?: string;
	onChangeText: (value: string) => void;
	disabled?: boolean;
	wordLimit?: WordLimit;
	prepTime?: ResponsePrepTime;
	timeLimit?: number;
	initialElapsedSeconds?: number;
	allowAudioReplay?: boolean;
	onResponseComplete?: () => void;
	onAudioEnded?: () => void;
};

const SummarizeSpokenText = ({
	instruction,
	subInstruction,
	audioUri,
	value,
	onChangeText,
	disabled,
	wordLimit,
	prepTime,
	timeLimit,
	initialElapsedSeconds,
	allowAudioReplay,
	onResponseComplete,
	onAudioEnded,
}: Props) => {
	const { isMobile } = useDevice();
	const audioPlayerRef = useRef<AudioPlayerRef | null>(null);
	const isTimed = prepTime != null || timeLimit != null;
	const playOnce = !allowAudioReplay;

	const writingTimer = useWritingTimer({
		timeLimit,
		startTrigger: "manual",
		disabled,
		initialElapsedSeconds,
		onExpire: onResponseComplete,
	});

	const { remainingPrepSeconds, showBanner } = usePrepSequence({
		prepTime,
		hasAudioStimulus: true,
		hasRecorder: false,
		audioPlayerRef,
		onAudioEnded: () => {
			writingTimer.start();
			onAudioEnded?.();
		},
		enabled: isTimed && !disabled,
	});

	return (
		<Box gap={isMobile ? 16 : 24}>
			<Box gap={8}>
				<ItemInstruction instruction={instruction} />
				{subInstruction ? <SubInstruction text={subInstruction} /> : null}
				{showBanner ? (
					<PrepCountdownBanner
						remainingSeconds={remainingPrepSeconds}
						playOnce={playOnce}
					/>
				) : null}
				<AudioPlayerContainer
					ref={audioPlayerRef}
					uri={audioUri}
					playOnce={playOnce}
				/>
			</Box>
			<Box gap={8}>
				<WritingInput
					value={value}
					onChangeText={onChangeText}
					disabled={disabled || writingTimer.isExpired}
					isAnswered={disabled}
					wordLimit={wordLimit}
					showWordLimitError={false}
					autoExpand
				/>
			</Box>
		</Box>
	);
};

export default SummarizeSpokenText;
